import { useUser } from './core/store/user.store';
import { useSiteFeatures } from './core/store/siteFeatures.store';
import { getCurrentSeasonPath, getPostLoginLandingPath, readStoredPreferences } from './core/store/preferences.store';
import { createRouter, createMemoryHistory, createWebHistory } from 'vue-router';
import { isSiteLockEnabled, isSiteUnlocked } from './core/security/siteAccess';
import { normalizeAnimeOverviewSection } from './core/utils/animePaths';
import { normalizeRoutePath } from './core/utils/pagination';
import { isExtensionSignupPath, isPrivacyPolicyPath, isMcpAuthorizationPath } from './core/utils/privacyRoute';
import { isPremiumSubscriptionPagesEnabled } from './modules/subscriptions/config/premiumCatalog';

const HomeView = () => import(/* webpackChunkName: "home" */ './modules/home/views/HomeView.vue');
const AnimeListView = () => import(/* webpackChunkName: "anime-list" */ './modules/animelist/views/AnimeListView.vue');
const AnimeDetailsView = () => import(/* webpackChunkName: "anime-overview" */ './modules/animelist/views/AnimeDetailsView.vue');
const DetailsView = () => import(/* webpackChunkName: "anime-overview" */ './modules/animelist/views/DetailsView.vue');
const EpisodesView = () => import(/* webpackChunkName: "anime-overview" */ './modules/episodes/views/EpisodesView.vue');
const CharactersView = () => import(/* webpackChunkName: "anime-overview" */ './modules/characters/views/CharactersView.vue');
const StaffView = () => import(/* webpackChunkName: "anime-overview" */ './modules/staff/views/StaffView.vue');
const WatchOrderView = () => import(/* webpackChunkName: "anime-overview" */ './modules/animelist/views/WatchOrderView.vue');
const ThemeSongsView = () => import(/* webpackChunkName: "anime-overview" */ './modules/animelist/views/ThemeSongsView.vue');
const AnimeBookmarksView = () => import(/* webpackChunkName: "bookmarks" */ './modules/animelist/views/AnimeBookmarksView.vue');
const TrackerActivityView = () => import(/* webpackChunkName: "tracker-activity" */ './modules/bookmarks/tracker/views/TrackerActivityView.vue');
const WatchingView = () => import(/* webpackChunkName: "watching" */ './modules/bookmarks/views/WatchingView.vue');
const SharedBookmarksView = () => import(/* webpackChunkName: "bookmarks" */ './modules/bookmarks/views/SharedBookmarksView.vue');
const ReleasesView = () => import(/* webpackChunkName: "releases" */ './modules/animelist/views/ReleasesView.vue');
const ArchiveView = () => import(/* webpackChunkName: "archive" */ './modules/animelist/views/ArchiveView.vue');
const DiscoverView = () => import(/* webpackChunkName: "discover" */ './modules/animelist/views/DiscoverView.vue');
const AnimeSearchView = () => import(/* webpackChunkName: "anime-search" */ './modules/search/views/AnimeSearchView.vue');
const NewsView = () => import(/* webpackChunkName: "news" */ './modules/news/views/NewsView.vue');
const NewsArticleView = () => import(/* webpackChunkName: "news" */ './modules/news/views/NewsArticleView.vue');
const WatchOrderSelectorView = () => import(/* webpackChunkName: "watch-order-selector" */ './modules/watchorder/views/WatchOrderSelectorView.vue');
const WatchOrderHubView = () => import(/* webpackChunkName: "watch-order-guides" */ './modules/watchorder/views/WatchOrderHubView.vue');
const SettingsView = () => import(/* webpackChunkName: "settings" */ './modules/settings/views/SettingsView.vue');
const ProfileImageView = () => import(/* webpackChunkName: "profile-image" */ './modules/profile/views/ProfileImageView.vue');
const AnnouncementsView = () => import(/* webpackChunkName: "announcements" */ './modules/announcements/views/AnnouncementsView.vue');
const SiteAccessView = () => import(/* webpackChunkName: "site-access" */ './modules/security/views/SiteAccessView.vue');
const PrivacyView = () => import(/* webpackChunkName: "privacy" */ './modules/legal/views/PrivacyView.vue');
const TermsView = () => import(/* webpackChunkName: "legal" */ './modules/legal/views/TermsView.vue');
const RefundPolicyView = () => import(/* webpackChunkName: "legal" */ './modules/legal/views/RefundPolicyView.vue');
const ExtensionSignupView = () => import(/* webpackChunkName: "extension-signup" */ './modules/auth/views/ExtensionSignupView.vue');
const McpAuthorizeView = () => import(/* webpackChunkName: "mcp-authorize" */ './modules/auth/views/McpAuthorizeView.vue');
const PricingView = () => import(/* webpackChunkName: "subscriptions" */ './modules/subscriptions/views/PricingView.vue');
const BillingView = () => import(/* webpackChunkName: "subscriptions" */ './modules/subscriptions/views/BillingView.vue');
const TrackerView = () => import(/* webpackChunkName: "subscriptions" */ './modules/subscriptions/views/TrackerView.vue');
const ContactView = () => import(/* webpackChunkName: "subscriptions" */ './modules/subscriptions/views/ContactView.vue');

const isServer = typeof window === 'undefined';
const history = isServer ? createMemoryHistory() : createWebHistory();
const siteLockEnabled = isSiteLockEnabled();

const getDefaultSeasonPath = () => getCurrentSeasonPath();
const getStoredOverviewTab = () => {
    if (isServer) {
        return 'details';
    }
    return normalizeAnimeOverviewSection(readStoredPreferences().overviewTab);
};
const getStoredPostLoginLandingPath = () => {
    if (isServer) {
        return '/';
    }
    return getPostLoginLandingPath(readStoredPreferences().postLoginLanding);
};
const getSafeRedirectPath = (value) => {
    if (typeof value !== 'string') {
        return null;
    }
    if (!value.startsWith('/') || value === '/') {
        return null;
    }
    return normalizeRoutePath(value);
};

const rootRoute = siteLockEnabled
    ? {
        path: "/",
        name: 'SiteAccessView',
        component: SiteAccessView,
        components: {
            AnimeListView: SiteAccessView
        },
        meta: { siteAccessRoute: true }
    }
    : {
        path: '/',
        name: 'HomeView',
        component: HomeView,
        components: {
            AnimeListView: HomeView
        }
    };

const routes = [
    rootRoute,
    {
        path: '/mcp/authorize',
        name: 'McpAuthorizeView',
        component: McpAuthorizeView,
        components: { AnimeListView: McpAuthorizeView },
        meta: { publicWhenSiteLocked: true, scrollToTopOnEntry: true }
    },
    {
        path: '/bookmarks/tracker', name: 'TrackerActivityView', component: TrackerActivityView,
        components: { AnimeListView: TrackerActivityView },
        meta: { scrollToTopOnEntry: true }
    },
    {
        path: '/seasons',
        redirect: () => ({
            path: getCurrentSeasonPath()
        })
    },
    {
        path: '/seasons/:season/:year',
        name: 'AnimeListView',
        component: AnimeListView,
        components: {
            AnimeListView
        },
    }, {
        path: '/discover',
        name: 'DiscoverView',
        component: DiscoverView,
        components: {
            AnimeListView: DiscoverView
        }
    }, {
        path: '/search',
        name: 'AnimeSearchView',
        component: AnimeSearchView,
        components: {
            AnimeListView: AnimeSearchView
        }
    }, {
        path: '/releases',
        name: 'ReleasesView',
        component: ReleasesView,
        components: {
            ReleasesView
        }
    }, {
        path: '/news',
        name: 'NewsView',
        component: NewsView,
        meta: {
            requiresSiteFeature: 'news'
        },
        components: {
            AnimeListView: NewsView
        }
    }, {
        path: '/news/:newsId/:slug?',
        name: 'NewsArticleView',
        component: NewsArticleView,
        meta: {
            requiresSiteFeature: 'news',
            scrollToTopOnEntry: true
        },
        components: {
            AnimeListView: NewsArticleView
        }
    }, {
        path: '/watch-order',
        name: 'WatchOrderSelectorView',
        component: WatchOrderSelectorView,
        components: {
            AnimeListView: WatchOrderSelectorView
        },
        meta: {
            scrollToTopOnEntry: true
        }
    }, {
        path: '/watch-order/:seriesSlug/:layout?',
        name: 'WatchOrderHubView',
        component: WatchOrderHubView,
        components: {
            AnimeListView: WatchOrderHubView
        },
        meta: {
            scrollToTopOnEntry: true
        }
    }, {
        path: '/archive',
        name: 'ArchiveView',
        component: ArchiveView,
        components: {
            AnimeListView: ArchiveView
        }
    }, {
        path: '/archive/:season/:year',
        name: 'ArchiveSeasonView',
        component: AnimeListView,
        components: {
            AnimeListView
        }
    }, {
        path: '/tracker',
        name: 'TrackerView',
        component: TrackerView,
        components: {
            AnimeListView: TrackerView
        },
        meta: {
            publicWhenSiteLocked: true,
            scrollToTopOnEntry: true
        }
    }, {
        path: '/contact',
        name: 'ContactView',
        component: ContactView,
        components: {
            AnimeListView: ContactView
        },
        meta: {
            publicWhenSiteLocked: true,
            scrollToTopOnEntry: true
        }
    }, {
        path: '/privacy',
        name: 'PrivacyView',
        component: PrivacyView,
        components: {
            AnimeListView: PrivacyView
        },
        meta: {
            publicWhenSiteLocked: true,
            scrollToTopOnEntry: true
        }
    }, {
        path: '/extension-signup',
        name: 'ExtensionSignupView',
        component: ExtensionSignupView,
        components: {
            AnimeListView: ExtensionSignupView
        },
        meta: {
            publicWhenSiteLocked: true,
            scrollToTopOnEntry: true
        }
    }, {
        path: '/terms',
        name: 'TermsView',
        component: TermsView,
        components: {
            AnimeListView: TermsView
        },
        meta: {
            publicWhenSiteLocked: true,
            requiresPremiumSubscriptionPages: true,
            scrollToTopOnEntry: true
        }
    }, {
        path: '/refund-policy',
        name: 'RefundPolicyView',
        component: RefundPolicyView,
        components: {
            AnimeListView: RefundPolicyView
        },
        meta: {
            publicWhenSiteLocked: true,
            requiresPremiumSubscriptionPages: true,
            scrollToTopOnEntry: true
        }
    }, {
        path: '/pricing',
        name: 'PricingView',
        component: PricingView,
        components: {
            AnimeListView: PricingView
        },
        meta: {
            requiresPremiumSubscriptionPages: true,
            scrollToTopOnEntry: true
        }
    }, {
        path: '/account/billing',
        name: 'BillingView',
        component: BillingView,
        components: {
            AnimeListView: BillingView
        },
        meta: {
            requiresPremiumSubscriptionPages: true,
            scrollToTopOnEntry: true
        }
    }, {
        path: '/settings',
        name: 'SettingsView',
        component: SettingsView,
        components: {
            AnimeListView: SettingsView
        },
        meta: { requiresAuth: true }
    }, {
        path: '/announcements',
        name: 'AnnouncementsView',
        component: AnnouncementsView,
        components: {
            AnimeListView: AnnouncementsView
        },
        meta: {
            requiresAuth: true,
            scrollToTopOnEntry: true
        }
    }, {
        path: '/profile/image',
        name: 'ProfileImageView',
        component: ProfileImageView,
        components: {
            AnimeListView: ProfileImageView
        },
        meta: { requiresAuth: true }
    }, {
        path: '/bookmarks',
        name: 'AnimeBookmarksView',
        component: AnimeBookmarksView,
        components: {
            AnimeBookmarksView
        },
        meta: { requiresAuth: true }
    }, {
        path: '/shared/bookmarks/:token',
        name: 'SharedBookmarksView',
        component: SharedBookmarksView,
        components: {
            AnimeListView: SharedBookmarksView
        }
    }, {
        path: '/watching/:anime_id/:anime(.*)',
        name: 'WatchingView',
        component: WatchingView,
        components: {
            WatchingView
        },
        meta: { requiresAuth: true }
    }, {
        path: '/overview/:anime_id/:anime',
        name: 'AnimeDetailsView',
        meta: { scrollToTopOnEntry: true },
        redirect: (to) => ({
            path: normalizeRoutePath(`/overview/${to.params.anime_id}/${to.params.anime}/${getStoredOverviewTab()}`)
        }),
        component: AnimeDetailsView,
        components: {
            AnimeDetailsView
        },
        children: [{
            path: 'details',
            name: 'DetailsView',
            component: DetailsView
        },{
            path: 'episodes',
            name: 'EpisodesView',
            component: EpisodesView
        }, {
            path: 'characters',
            name: 'CharactersView',
            component: CharactersView
        }, {
            path: 'staff',
            name: 'StaffView',
            component: StaffView
        }, {
            path: 'watch-order',
            name: 'WatchOrderView',
            component: WatchOrderView
        }, {
            path: 'themes',
            name: 'ThemeSongsView',
            component: ThemeSongsView
        }]
    }
];

const router = createRouter({
    history,
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }

        if (
            ['PrivacyView', 'TermsView', 'RefundPolicyView'].includes(to.name)
            && /^#[a-z0-9-]+$/u.test(`${to.hash || ''}`)
        ) {
            return {
                el: to.hash,
                behavior: 'auto'
            };
        }

        const shouldScrollToTop = to.matched.some((record) => record.meta.scrollToTopOnEntry === true);
        const staysOnSameAnimeOverview = (
            from.matched.some((record) => record.name === 'AnimeDetailsView')
            && to.matched.some((record) => record.name === 'AnimeDetailsView')
            && `${from.params.anime_id || ''}` === `${to.params.anime_id || ''}`
        );

        if (shouldScrollToTop && !staysOnSameAnimeOverview) {
            return {
                top: 0,
                left: 0,
                behavior: 'auto'
            };
        }

        return false;
    }
});

router.beforeEach(async (to, from, next) => {
    const cleanDocumentTargetPath = normalizeRoutePath(to.path);
    const isCleanDocumentRoute = (
        isPrivacyPolicyPath(to.path)
        || isExtensionSignupPath(to.path)
        || isMcpAuthorizationPath(to.path)
    );
    const isSpaEntryToCleanDocumentRoute = (
        !isServer
        && isCleanDocumentRoute
        && Boolean(from?.name)
        && window.location?.pathname !== cleanDocumentTargetPath
    );
    if (isSpaEntryToCleanDocumentRoute) {
        const targetUrl = new URL(to.fullPath, window.location.origin);
        targetUrl.pathname = cleanDocumentTargetPath;
        window.location.assign(targetUrl.href);
        return;
    }

    const normalizedPath = normalizeRoutePath(to.path);
    if (normalizedPath !== to.path) {
        next({
            path: normalizedPath,
            query: to.query,
            hash: to.hash,
            replace: true
        });
        return;
    }

    const requiresPremiumSubscriptionPages = to.matched.some(
        (record) => record.meta.requiresPremiumSubscriptionPages === true
    );
    if (requiresPremiumSubscriptionPages && !isPremiumSubscriptionPagesEnabled()) {
        next({ path: '/', replace: true });
        return;
    }

    if (isSiteLockEnabled()) {
        const unlocked = isSiteUnlocked();
        const isSiteAccessRoute = to.meta.siteAccessRoute === true;
        const isPublicWhenSiteLocked = to.meta.publicWhenSiteLocked === true;

        if (!unlocked && !isSiteAccessRoute && !isPublicWhenSiteLocked) {
            next({
                name: 'SiteAccessView',
                query: {
                    redirect: to.fullPath
                }
            });
            return;
        }

        if (unlocked && isSiteAccessRoute) {
            const redirectPath = getSafeRedirectPath(to.query.redirect);
            next(redirectPath || getDefaultSeasonPath());
            return;
        }
    }

    const requiredSiteFeature = `${to.meta?.requiresSiteFeature || ''}`.trim().toLowerCase();
    if (requiredSiteFeature) {
        const siteFeatures = useSiteFeatures();
        await siteFeatures.ensureLoaded({ force: true });
        if (!siteFeatures.isEnabled(requiredSiteFeature)) {
            // A disabled preferred destination must not bounce through Home and
            // repeat the initial signed-in landing redirect. Keep the preference.
            const isPreferredLanding = useUser().isAuthenticated
                && getStoredPostLoginLandingPath() === normalizedPath;
            next({
                path: isPreferredLanding ? getDefaultSeasonPath() : '/',
                replace: true
            });
            return;
        }
    }

    const userStore = useUser();
    const isInitialNavigation = !from?.name && (!Array.isArray(from?.matched) || from.matched.length === 0);
    const hasAuthContinuation = ['verifyEmailToken', 'resetPasswordToken']
        .some((key) => `${to.query?.[key] || ''}`.trim());
    if (
        to.name === 'HomeView'
        && userStore.isAuthenticated
        && isInitialNavigation
        // The auth dialog must consume emailed tokens before applying the saved landing page.
        && !hasAuthContinuation
    ) {
        const landingPath = getStoredPostLoginLandingPath();
        if (landingPath && landingPath !== '/') {
            next({ path: landingPath, replace: true });
            return;
        }
    }

    if (to.meta.requiresAuth && !userStore.isAuthenticated) {
        next({ path: getCurrentSeasonPath() });
    } else {
        next();
    }

});

export default router;
